import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';

/**
 * Position options for the tooltip
 */
export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

/**
 * Props interface for Tooltip component
 */
export interface TooltipProps {
  /** The content to display inside the tooltip */
  content: string;
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** Position of the tooltip relative to the trigger element */
  position?: TooltipPosition;
  /** Additional CSS classes for the tooltip */
  className?: string;
  /** Delay before showing tooltip in ms (default: 0) */
  showDelay?: number;
  /** Delay before hiding tooltip in ms (default: 100) */
  hideDelay?: number;
}

// ============================================================
// Singleton Tooltip Manager (module-level, no React context)
// ============================================================

/**
 * Module-level singleton manager that ensures only one tooltip is visible
 * at a time across the entire application. Uses a custom event pattern
 * so each Tooltip instance can broadcast "I'm showing" and listen for
 * "another tooltip is showing — hide me".
 */
type TooltipShowHandler = (activeId: string) => void;
const listeners = new Set<TooltipShowHandler>();

/** Broadcast that a tooltip with the given id is now visible. */
function broadcastTooltipShow(id: string): void {
  listeners.forEach((handler) => handler(id));
}

/** Subscribe to tooltip show events. Returns an unsubscribe function. */
function onTooltipShow(handler: TooltipShowHandler): () => void {
  listeners.add(handler);
  return () => {
    listeners.delete(handler);
  };
}

// Global counter for unique tooltip instance IDs
let tooltipIdCounter = 0;

/**
 * Calculate tooltip position based on trigger element and desired position
 */
function calculatePosition(
  triggerRect: DOMRect,
  position: TooltipPosition,
  tooltipWidth: number = 0
): { top: number; left: number; transform: string } {
  const gap = 8; // 8px gap between trigger and tooltip

  switch (position) {
    case 'top':
      return {
        top: triggerRect.top - gap,
        left: triggerRect.left + triggerRect.width / 2,
        transform: 'translateX(-50%) translateY(-100%)',
      };
    case 'bottom':
      return {
        top: triggerRect.bottom + gap,
        left: triggerRect.left + triggerRect.width / 2,
        transform: 'translateX(-50%)',
      };
    case 'left':
      return {
        top: triggerRect.top + triggerRect.height / 2,
        left: triggerRect.left - gap - tooltipWidth,
        transform: 'translateY(-50%)',
      };
    case 'right':
      return {
        top: triggerRect.top + triggerRect.height / 2,
        left: triggerRect.right + gap,
        transform: 'translateY(-50%)',
      };
    default:
      return {
        top: triggerRect.top - gap,
        left: triggerRect.left + triggerRect.width / 2,
        transform: 'translateX(-50%) translateY(-100%)',
      };
  }
}

/**
 * TooltipPortal - Renders tooltip in document body to avoid overflow clipping
 */
function TooltipPortal({
  content,
  triggerRef,
  position,
  className,
}: {
  content: string;
  triggerRef: React.RefObject<HTMLElement | null>;
  position: TooltipPosition;
  className?: string;
}) {
  const [coords, setCoords] = useState({ top: 0, left: 0, transform: '' });
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = tooltipRef.current?.offsetWidth || 0;
      setCoords(calculatePosition(rect, position, tooltipWidth));
    }
  }, [triggerRef, position]);

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      className={cn(
        'fixed z-[200]',
        'bg-white',
        'border border-[#EBEBEB]',
        'rounded-[4px]',
        'py-2 px-4',
        typography.body,
        'text-foreground',
        'whitespace-nowrap',
        'pointer-events-none',
        className
      )}
      style={{
        top: coords.top,
        left: coords.left,
        transform: coords.transform,
        boxShadow: '0px 4px 4px 0px #BBBBBB40',
      }}
    >
      {content}
    </div>,
    document.body
  );
}

/**
 * Tooltip - Displays contextual information on hover/focus
 *
 * Renders a tooltip that appears when the user hovers over or focuses on the
 * trigger element. Uses a portal to render in document body to avoid overflow
 * clipping issues.
 *
 * Features:
 * - **Singleton**: Only one tooltip is visible at a time across the app.
 *   When a new tooltip shows, all others hide immediately.
 * - **Scroll dismiss**: When the user scrolls such that the trigger element
 *   is no longer under the cursor, the tooltip dismisses automatically.
 *
 * Styling matches the Service Directory filter tooltips:
 * - Background: #FFFFFF
 * - Border: 1px solid #EBEBEB
 * - Border Radius: 4px
 * - Padding: 8px 16px
 * - Box Shadow: 0px 4px 4px 0px #BBBBBB40
 *
 * @example
 * ```tsx
 * import { Tooltip } from '@/components';
 *
 * <Tooltip content="Share resource">
 *   <button>
 *     <ShareIcon />
 *   </button>
 * </Tooltip>
 * ```
 */
export function Tooltip({
  content,
  children,
  position = 'top',
  className,
  showDelay = 0,
  hideDelay = 100,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const instanceIdRef = useRef<string>('');
  const mousePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Assign a unique ID to this tooltip instance on mount
  useEffect(() => {
    tooltipIdCounter += 1;
    instanceIdRef.current = `tooltip-${tooltipIdCounter}`;
  }, []);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // -------------------------------------------
  // Singleton: listen for other tooltips showing
  // -------------------------------------------
  useEffect(() => {
    const unsubscribe = onTooltipShow((activeId) => {
      if (activeId !== instanceIdRef.current) {
        // Another tooltip is showing — hide this one immediately
        if (showTimeoutRef.current) {
          clearTimeout(showTimeoutRef.current);
          showTimeoutRef.current = null;
        }
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        setIsVisible(false);
      }
    });

    return unsubscribe;
  }, []);

  // -------------------------------------------
  // Scroll dismiss: hide tooltip when trigger
  // scrolls out from under the cursor
  // -------------------------------------------
  useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const { x, y } = mousePositionRef.current;

      // Check if the mouse position is still within the trigger element bounds
      const isMouseOverTrigger =
        x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

      if (!isMouseOverTrigger) {
        // Clear any pending show/hide timeouts
        if (showTimeoutRef.current) {
          clearTimeout(showTimeoutRef.current);
          showTimeoutRef.current = null;
        }
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [isVisible]);

  const showTooltip = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      // Broadcast to all other tooltips that this one is now active
      broadcastTooltipShow(instanceIdRef.current);
    }, showDelay);
  }, [showDelay]);

  const hideTooltip = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, hideDelay);
  }, [hideDelay]);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      // Track the mouse position for scroll dismiss
      mousePositionRef.current = { x: event.clientX, y: event.clientY };
      showTooltip();
    },
    [showTooltip]
  );

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    // Continuously update mouse position while hovering for scroll dismiss
    mousePositionRef.current = { x: event.clientX, y: event.clientY };
  }, []);

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  const handleFocus = useCallback(() => {
    showTooltip();
  }, [showTooltip]);

  const handleBlur = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  // Handle touch for mobile - toggle on tap
  const handleTouchStart = useCallback(() => {
    if (isVisible) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
      // Broadcast to all other tooltips
      broadcastTooltipShow(instanceIdRef.current);
    }
  }, [isVisible]);

  // Close tooltip when clicking outside on mobile
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isVisible]);

  return (
    <div
      ref={triggerRef}
      className="inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onTouchStart={handleTouchStart}
    >
      {children}
      {isVisible && (
        <TooltipPortal
          content={content}
          triggerRef={triggerRef}
          position={position}
          className={className}
        />
      )}
    </div>
  );
}

// Export the singleton management functions
// Public API for other tooltip implementations to participate in singleton coordination
export { broadcastTooltipShow, onTooltipShow };
// Legacy exports used by SubcategoryPill (kept for backward compat)
export { broadcastTooltipShow as _broadcastTooltipShow, listeners as _listeners };

export default Tooltip;
